import { useEffect, useState } from "react";
import "@/App.css";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Copy, Check, MapPin } from "lucide-react";
import { Toaster, toast } from "sonner";

import { ARTICLE, UI, IMAGES } from "@/data/article";
import { useSmoothScroll } from "@/components/editorial/useSmoothScroll";
import { ScrollProgress } from "@/components/editorial/ScrollProgress";
import { LanguageToggle } from "@/components/editorial/LanguageToggle";
import { KineticHero } from "@/components/editorial/KineticHero";
import { ParallaxImage } from "@/components/editorial/ParallaxImage";
import { Reveal } from "@/components/editorial/Reveal";
import { Marquee } from "@/components/editorial/Marquee";

const SectionLabel = ({ children }) => (
  <div className="mb-10 flex items-center gap-4 text-xs uppercase tracking-[0.3em]" style={{ color: "var(--accent)" }}>
    <span className="inline-block h-px w-8" style={{ background: "var(--accent)" }} />
    {children}
  </div>
);

const Chapter = ({ chapter, dropcap, image, caption }) => (
  <section className="mx-auto max-w-[1500px] px-5 py-20 md:px-10 md:py-28">
    <div className="grid grid-cols-1 gap-10 lg:grid-cols-[0.9fr_1.1fr]">
      <Reveal>
        <div className="lg:sticky lg:top-28">
          <p className="font-display text-[22vw] leading-none tracking-tighter lg:text-[9vw]" style={{ color: "transparent", WebkitTextStroke: "1.5px var(--accent)" }}>
            {chapter.n}
          </p>
        </div>
      </Reveal>
      <Reveal delay={0.1}>
        <h2 className="font-display text-3xl font-bold leading-tight tracking-tight md:text-5xl">{chapter.heading}</h2>
        <div className="mt-8 max-w-2xl space-y-6 text-lg leading-relaxed" style={{ color: "var(--muted)" }}>
          {chapter.paragraphs.map((p, i) => (
            <p key={i} className={dropcap && i === 0 ? "dropcap" : ""}>{p}</p>
          ))}
        </div>
        {image && (
          <div className="mt-12">
            <ParallaxImage src={image} alt={caption || ""} ratio="aspect-[16/10]" />
            {caption && <p className="mt-3 text-xs uppercase tracking-[0.2em]" style={{ color: "var(--muted)" }}>{caption}</p>}
          </div>
        )}
      </Reveal>
    </div>
  </section>
);

const FaqItem = ({ q, a, testid }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b" style={{ borderColor: "var(--line)" }}>
      <button
        data-testid={testid}
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-6 py-6 text-left"
      >
        <span className="font-display text-xl md:text-2xl">{q}</span>
        <motion.span animate={{ rotate: open ? 45 : 0 }} transition={{ duration: 0.3 }} style={{ color: "var(--accent)" }}>
          <Plus size={22} strokeWidth={1.5} />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <p className="max-w-2xl pb-7 text-lg leading-relaxed" style={{ color: "var(--muted)" }}>{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

function App() {
  useSmoothScroll();
  const [lang, setLang] = useState(() => {
    const saved = localStorage.getItem("w16-lang");
    return saved === "fr" || saved === "en" ? saved : "en";
  });
  const [copied, setCopied] = useState(false);

  const d = ARTICLE[lang];
  const ui = UI[lang];

  useEffect(() => {
    localStorage.setItem("w16-lang", lang);
    document.documentElement.lang = lang;
    document.title = d.title;
  }, [lang, d.title]);

  const copyCode = () => {
    navigator.clipboard?.writeText(d.booking.code);
    setCopied(true);
    toast.success(lang === "fr" ? `Code ${d.booking.code} copié` : `Code ${d.booking.code} copied`);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="App grain">
      <Toaster position="bottom-center" theme="light" />
      <ScrollProgress />

      {/* Sticky header */}
      <header className="fixed left-0 top-0 z-50 flex w-full items-center justify-between px-5 py-4 md:px-10">
        <a
          href="#top"
          data-testid="brand-link"
          className="font-display text-lg font-bold tracking-tight md:text-xl"
        >
          {ui.brand}
          <span style={{ color: "var(--accent)" }}>.</span>
        </a>
        <LanguageToggle lang={lang} onChange={setLang} />
      </header>

      <div id="top" />

      <AnimatePresence mode="wait">
        <motion.main
          key={lang}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
        >
          <KineticHero data={d} ui={ui} image={IMAGES.hero} />

          {/* Lede */}
          <section className="mx-auto max-w-3xl px-5 py-20 md:py-28">
            <Reveal>
              <p className="dropcap font-display text-2xl leading-snug md:text-[2rem] md:leading-snug">
                {d.lede}
              </p>
            </Reveal>
          </section>

          <Chapter chapter={d.chapters[0]} image={IMAGES.officeDistrict} caption={null} />

          <Chapter chapter={d.chapters[1]} />

          {/* Three areas */}
          <section className="mx-auto max-w-[1500px] px-5 pb-24 md:px-10">
            <div className="grid grid-cols-1 gap-x-8 gap-y-14 md:grid-cols-3">
              {d.areas.map((area, i) => (
                <Reveal key={i} delay={i * 0.08}>
                  <ParallaxImage src={[IMAGES.areaMontgomery, IMAGES.areaLeopold, IMAGES.areaEtterbeek][i]} alt={area.name} ratio="aspect-[3/4]" objectPosition={i === 0 ? "72% center" : "center"} />
                  <h3 className="mt-6 font-display text-2xl font-bold leading-tight">
                    <span style={{ color: "var(--accent)" }} className="mr-2 text-base align-top">{`0${i + 1}`}</span>
                    {area.name}
                  </h3>
                  <p className="mt-3 leading-relaxed" style={{ color: "var(--muted)" }}>{area.text}</p>
                </Reveal>
              ))}
            </div>
          </section>

          {/* Full-bleed bandeau */}
          <section className="px-5 md:px-10">
            <Reveal>
              <div className="relative">
                <ParallaxImage src={IMAGES.bandeau} alt={d.bandeauCaption} ratio="aspect-[21/9]" />
                {/* Marqueur emplacement W16 */}
                <motion.div
                  data-testid="w16-marker"
                  className="absolute z-20 flex items-center gap-2.5"
                  style={{ left: "6.5%", top: "52%", transform: "translateY(-50%)" }}
                  initial={{ opacity: 0, scale: 0.5 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.5, type: "spring", stiffness: 260, damping: 16 }}
                >
                  <span className="relative flex h-5 w-5 shrink-0">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-75" style={{ background: "var(--accent)" }} />
                    <span className="relative inline-flex h-5 w-5 rounded-full border-[2.5px] border-white shadow-lg" style={{ background: "var(--accent)" }} />
                  </span>
                  <span className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-white shadow-xl" style={{ background: "var(--ink)" }}>
                    <MapPin size={13} style={{ color: "var(--accent)" }} />
                    Whitlock16
                  </span>
                </motion.div>
              </div>
              <p className="mt-4 max-w-2xl text-sm italic" style={{ color: "var(--muted)" }}>{d.bandeauCaption}</p>
            </Reveal>
          </section>

          {/* Pull quote */}
          <section className="mx-auto max-w-4xl px-5 py-28 text-center">
            <Reveal>
              <p className="font-display text-3xl italic leading-tight md:text-5xl md:leading-tight">
                <span style={{ color: "var(--accent)" }}>“</span>
                {d.pullQuote}
                <span style={{ color: "var(--accent)" }}>”</span>
              </p>
            </Reveal>
          </section>

          <Marquee />

          {/* Travel table */}
          <section className="mx-auto max-w-[1500px] px-5 py-24 md:px-10 md:py-32">
            <div className="grid grid-cols-1 gap-12 lg:grid-cols-[0.8fr_1.2fr]">
              <Reveal>
                <SectionLabel>{ui.tocLabel}</SectionLabel>
                <h2 className="font-display text-3xl font-bold leading-tight md:text-5xl">{d.travel.heading}</h2>
                <p className="mt-6 max-w-md leading-relaxed" style={{ color: "var(--muted)" }}>{d.travel.intro}</p>
              </Reveal>
              <Reveal delay={0.1}>
                <table className="w-full border-collapse" data-testid="travel-table">
                  <thead>
                    <tr className="border-b-2" style={{ borderColor: "var(--ink)" }}>
                      {d.travel.cols.map((c, i) => (
                        <th key={i} className={`py-4 text-xs uppercase tracking-[0.2em] ${i === 0 ? "text-left" : "text-right"}`} style={{ color: "var(--accent)" }}>{c}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {d.travel.rows.map((row, i) => (
                      <tr key={i} className="border-b" style={{ borderColor: "var(--line)" }}>
                        {row.map((cell, j) => (
                          <td key={j} className={`py-5 ${j === 0 ? "font-display text-lg md:text-xl" : "text-right tabular-nums"}`} style={{ color: j === 0 ? "var(--ink)" : "var(--muted)" }}>{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Reveal>
            </div>
          </section>

          {/* Stay at Whitlock16 */}
          <section className="mx-auto max-w-[1500px] px-5 py-16 md:px-10">
            <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
              <Reveal>
                <div className="grid grid-cols-2 items-start gap-4">
                  <ParallaxImage src={IMAGES.studioMain} alt="Studio Whitlock16" ratio="aspect-[3/4]" className="mt-10" />
                  <div className="grid gap-4">
                    <ParallaxImage src={IMAGES.studioKitchen} alt="Cuisine équipée du studio" ratio="aspect-[4/3]" />
                    <ParallaxImage src={IMAGES.studioBath} alt="Salle de bain du studio" ratio="aspect-[4/3]" />
                  </div>
                </div>
              </Reveal>
              <Reveal delay={0.1}>
                <SectionLabel>Whitlock16</SectionLabel>
                <h2 className="font-display text-3xl font-bold leading-tight md:text-5xl">{d.stay.heading}</h2>
                <div className="mt-6 max-w-xl space-y-5 text-lg leading-relaxed" style={{ color: "var(--muted)" }}>
                  {d.stay.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
                </div>
                <div className="mt-8 flex items-center gap-2 text-sm" style={{ color: "var(--ink)" }}>
                  <MapPin size={16} style={{ color: "var(--accent)" }} />
                  {ui.footerAddress}
                </div>
                <div className="mt-8" data-testid="nearby-block">
                  <p className="mb-1 text-xs font-bold uppercase tracking-[0.25em]" style={{ color: "var(--accent)" }}>{d.stay.nearbyHeading}</p>
                  <p className="mb-4 text-sm" style={{ color: "var(--muted)" }}>{d.stay.nearbyNote}</p>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {d.stay.nearby.map((name, i) => (
                      <div key={i} className="flex items-center gap-3 rounded-2xl border p-2 pr-4 transition-colors duration-300 hover:border-[color:var(--accent)]" style={{ borderColor: "var(--line)" }}>
                        <img src={[IMAGES.schoolMontgomery, IMAGES.schoolIchec][i]} alt={name} className="h-14 w-14 shrink-0 rounded-xl object-cover" style={{ background: "var(--paper)" }} />
                        <span className="font-display text-sm leading-tight md:text-base">{name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>
            </div>
          </section>

          {/* Private garden bandeau */}
          <section className="px-5 py-10 md:px-10">
            <Reveal>
              <ParallaxImage src={IMAGES.garden} alt={ui.gardenCaption} ratio="aspect-[21/9]" />
              <p className="mt-4 max-w-2xl text-sm italic" style={{ color: "var(--muted)" }}>{ui.gardenCaption}</p>
            </Reveal>
          </section>

          {/* What matters */}
          <section className="mx-auto max-w-[1500px] px-5 py-24 md:px-10 md:py-32">
            <Reveal>
              <SectionLabel>{lang === "fr" ? "L'essentiel" : "The essentials"}</SectionLabel>
              <h2 className="max-w-4xl font-display text-3xl font-bold leading-tight md:text-5xl">{d.matters.heading}</h2>
              <p className="mt-6 max-w-2xl text-lg leading-relaxed" style={{ color: "var(--muted)" }}>{d.matters.intro}</p>
            </Reveal>
            <div className="mt-16 grid grid-cols-1 gap-10 md:grid-cols-3">
              {d.matters.items.map((item, i) => (
                <Reveal key={i} delay={i * 0.1}>
                  <ParallaxImage src={[IMAGES.matterFlex, IMAGES.matterWork, IMAGES.matterQuiet][i]} alt={item.title} ratio="aspect-[4/3]" />
                  <p className="mt-6 font-display text-5xl" style={{ color: "transparent", WebkitTextStroke: "1px var(--accent)" }}>{item.n}</p>
                  <h3 className="mt-3 font-display text-2xl font-bold">{item.title}</h3>
                  <p className="mt-3 leading-relaxed" style={{ color: "var(--muted)" }}>{item.text}</p>
                </Reveal>
              ))}
            </div>
          </section>

          {/* Booking */}
          <section className="px-5 pb-24 md:px-10">
            <Reveal>
              <div className="mx-auto max-w-[1500px] rounded-[2rem] px-6 py-16 md:px-16 md:py-24" style={{ background: "var(--ink)", color: "var(--bg)" }}>
                <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
                  <div>
                    <SectionLabel>{d.booking.heading}</SectionLabel>
                    <h2 className="font-display text-4xl font-bold leading-tight md:text-6xl">
                      {lang === "fr" ? "Réservez en direct." : "Book directly."}
                    </h2>
                    <p className="mt-6 max-w-lg text-lg leading-relaxed" style={{ color: "rgba(244,241,237,0.7)" }}>{d.booking.text}</p>
                  </div>
                  <div className="flex flex-col items-start gap-6 lg:items-end">
                    <div className="flex items-baseline gap-4">
                      <span className="font-display text-7xl md:text-8xl" style={{ color: "var(--accent)" }}>{d.booking.discount}</span>
                    </div>
                    <button
                      data-testid="copy-code-btn"
                      onClick={copyCode}
                      className="group flex items-center gap-4 rounded-full border px-6 py-4 transition-colors duration-300 hover:bg-[color:var(--accent)] hover:border-[color:var(--accent)]"
                      style={{ borderColor: "rgba(244,241,237,0.3)" }}
                    >
                      <span className="text-xs uppercase tracking-[0.2em] opacity-60">{ui.codeLabel}</span>
                      <span className="font-display text-2xl tracking-widest">{d.booking.code}</span>
                      {copied ? <Check size={18} /> : <Copy size={18} className="opacity-70" />}
                    </button>
                    <a
                      href="https://www.w16aparthotel.be"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="book-cta"
                      className="text-sm uppercase tracking-[0.2em] underline underline-offset-4 decoration-1 hover:opacity-70"
                    >
                      {ui.bookCta} →
                    </a>
                  </div>
                </div>
              </div>
            </Reveal>
          </section>

          {/* FAQ */}
          <section className="mx-auto max-w-4xl px-5 py-16 md:py-24">
            <Reveal>
              <SectionLabel>FAQ</SectionLabel>
              <h2 className="mb-10 font-display text-3xl font-bold leading-tight md:text-5xl">{d.faq.heading}</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="border-t" style={{ borderColor: "var(--line)" }}>
                {d.faq.items.map((item, i) => (
                  <FaqItem key={i} q={item.q} a={item.a} testid={`faq-item-${i}`} />
                ))}
              </div>
            </Reveal>
          </section>

          {/* Footer */}
          <footer className="border-t px-5 py-16 md:px-10" style={{ borderColor: "var(--line)" }}>
            <div className="mx-auto flex max-w-[1500px] flex-col justify-between gap-8 md:flex-row md:items-end">
              <div>
                <p className="font-display text-3xl font-bold tracking-tight md:text-4xl">
                  {ui.brand}<span style={{ color: "var(--accent)" }}>.</span>
                </p>
                <p className="mt-3 max-w-sm" style={{ color: "var(--muted)" }}>{ui.footerNote}</p>
              </div>
              <div className="text-sm" style={{ color: "var(--muted)" }}>
                <p>{ui.footerAddress}</p>
                <p className="mt-1">info@w16aparthotel.be</p>
                <p className="mt-4 text-xs uppercase tracking-[0.2em]">© {new Date().getFullYear()} Whitlock16</p>
              </div>
            </div>
          </footer>
        </motion.main>
      </AnimatePresence>
    </div>
  );
}

export default App;
